import React, { useEffect, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, Link, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import WorkOutlinedIcon from '@mui/icons-material/WorkOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import { AppButton } from '../../../components/UI';
import { TagList, StatusChips } from './CandidateList';
import CvPreviewModal from './CvPreviewModal';
import AddToCrmModal from './AddToCrmModal';
import { createCrmCandidate } from '../api';
import { asArray, formatDate, formatScore, formatUrl, initials, isBlank, linkedinUrl, scoreColors } from '../utils';

const SectionTitle = ({ children }) => (
  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.75 }}>
    {children}
  </Typography>
);

const InfoLine = ({ icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#334155', fontSize: '0.84rem', minWidth: 0, '& svg': { fontSize: 17, color: '#94a3b8', flexShrink: 0 } }}>
    {icon}
    <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</Box>
  </Box>
);

const Fact = ({ icon, label, value }) => (
  <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#f8fbfe', border: '1px solid #e2edf7', minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', '& svg': { fontSize: 15 } }}>
      {icon}
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
    </Box>
    <Typography noWrap title={value || ''} sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', mt: 0.25 }}>{value || '—'}</Typography>
  </Box>
);

const LINKEDIN_STATUS = {
  valid: { label: 'Vérifié', color: '#16a34a', icon: <CheckCircleIcon /> },
  invalid: { label: 'Introuvable', color: '#dc2626', icon: <ErrorOutlinedIcon /> },
  needs_review: { label: 'À vérifier', color: '#b45309', icon: <HelpOutlineIcon /> },
  missing: { label: 'Non trouvé', color: '#64748b', icon: <HelpOutlineIcon /> },
};

const linkBtnSx = { fontSize: '0.76rem', fontWeight: 600, color: '#03407b', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2, bgcolor: 'transparent', border: 'none', p: 0, fontFamily: 'inherit' };

const CandidateDetail = ({ candidate, actions, onCandidateChange, onAddedToCrm, onOpenCrmUrl, onClose, snackbarShowMessage }) => {
  const [checking, setChecking] = useState(false);
  const [editMode, setEditMode] = useState(null); // 'edit' | 'manual'
  const [linkInput, setLinkInput] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [cvOpen, setCvOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const [crmLoading, setCrmLoading] = useState(false);
  const autoChecked = useRef(new Set());

  const hasLinkedin = candidate && !isBlank(candidate.linkedin);

  const validate = async (c = candidate) => {
    if (!c) return;
    setChecking(true);
    try {
      const res = await actions.validateLinkedin(c.id);
      if (res) onCandidateChange({ ...c, linkedin_status: res.status, linkedin_verified_url: res.linkedin_verified_url ?? c.linkedin_verified_url });
    } catch (e) {
      snackbarShowMessage?.('Vérification LinkedIn impossible.', 'error');
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    setEditMode(null);
    if (candidate && !isBlank(candidate.linkedin) && candidate.linkedin_status === 'unchecked' && !autoChecked.current.has(candidate.id)) {
      autoChecked.current.add(candidate.id);
      validate(candidate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.id]);

  if (!candidate) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: 4 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: '#ebf4fb', color: '#03407b', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
          <PersonSearchOutlinedIcon />
        </Box>
        <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>Sélectionnez un candidat</Typography>
        <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.5, maxWidth: 300 }}>
          Son profil extrait du CV s'affichera ici : contact, compétences, résumé, aperçu du CV et ajout au CRM en un clic.
        </Typography>
      </Box>
    );
  }

  const saveLink = async () => {
    const url = linkInput.trim();
    if (!url) return;
    setSavingLink(true);
    try {
      if (editMode === 'manual') {
        await actions.verifyManual(candidate.id, url);
        onCandidateChange({ ...candidate, linkedin_status: 'valid', linkedin_verified_url: url });
      } else {
        await actions.updateLinkedin(candidate.id, url);
        const updated = { ...candidate, linkedin: url, linkedin_status: 'unchecked' };
        onCandidateChange(updated);
        validate(updated);
      }
      setEditMode(null);
    } catch (e) {
      snackbarShowMessage?.("Impossible d'enregistrer le lien LinkedIn.", 'error');
    } finally {
      setSavingLink(false);
    }
  };

  const submitCrm = async (form) => {
    setCrmLoading(true);
    try {
      const competences = [...asArray(candidate.technical_skills), ...asArray(candidate.programming_languages)].join(', ');
      const created = await createCrmCandidate({ competences, ...form });
      const url = `${window.location.origin}${created.path}`;
      try {
        await actions.markCrm(candidate.id, url);
      } catch (e) {
        // The CRM candidate exists; only the GoMail "added" flag failed.
      }
      onAddedToCrm({ ...candidate, added_to_crm: true, crm_url: url });
      setCrmOpen(false);
      snackbarShowMessage?.(created.updated ? 'Candidat déjà présent : fiche CRM mise à jour.' : 'Candidat ajouté au CRM.', 'success');
      onOpenCrmUrl(url);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.detail || e?.message;
      snackbarShowMessage?.(msg || "Échec de l'ajout au CRM.", 'error');
    } finally {
      setCrmLoading(false);
    }
  };

  const sc = scoreColors(candidate.score);
  const hasScore = typeof candidate.score === 'number' && !Number.isNaN(candidate.score);
  const breakdown = candidate.score_breakdown && typeof candidate.score_breakdown === 'object'
    ? Object.entries(candidate.score_breakdown).filter(([, v]) => typeof v === 'number').sort(([, a], [, b]) => b - a)
    : [];
  const liStatus = LINKEDIN_STATUS[candidate.linkedin_status];
  const github = formatUrl(candidate.github, 'github');
  const canAddToCrm = candidate.linkedin_status === 'valid';

  const crmBlockReason = !hasLinkedin
    ? 'Ajoutez le lien LinkedIn du candidat pour pouvoir le créer dans le CRM.'
    : ['invalid', 'needs_review'].includes(candidate.linkedin_status)
      ? 'Le lien LinkedIn semble invalide : corrigez-le ou vérifiez-le manuellement.'
      : 'Vérifiez le lien LinkedIn pour pouvoir créer le candidat dans le CRM.';

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #eef2f7', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 46, height: 46, borderRadius: '12px', background: 'linear-gradient(135deg, #0556a4, #03407b)', color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {initials(candidate.full_name)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>{candidate.full_name || 'Sans nom'}</Typography>
          <Typography noWrap sx={{ fontSize: '0.78rem', color: '#64748b' }}>
            {asArray(candidate.roles_held)[0] || 'Poste non précisé'} · reçu le {formatDate(candidate.date)}
          </Typography>
        </Box>
        {hasScore && (
          <Tooltip title="Pertinence par rapport à votre recherche">
            <Chip label={formatScore(candidate.score)} sx={{ fontWeight: 800, bgcolor: sc.bg, color: sc.color }} />
          </Tooltip>
        )}
        {onClose && (
          <Tooltip title="Fermer (Échap)">
            <IconButton size="small" onClick={onClose} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', color: '#64748b', '&:hover': { bgcolor: '#f0f7fd', color: '#03407b' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusChips candidate={candidate} />
          {candidate.cv_file_path && (
            <AppButton size="small" variant="import" startIcon={<PictureAsPdfOutlinedIcon />} onClick={() => setCvOpen(true)} sx={{ ml: 'auto' }}>
              Voir le CV
            </AppButton>
          )}
        </Box>

        <Box>
          <SectionTitle>Contact</SectionTitle>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 0.75 }}>
            <InfoLine icon={<MailOutlineIcon />}>
              {isBlank(candidate.email) ? '—' : <Link href={`mailto:${candidate.email}`} underline="hover" sx={{ color: '#03407b' }}>{candidate.email}</Link>}
            </InfoLine>
            <InfoLine icon={<PhoneOutlinedIcon />}>
              {isBlank(candidate.phone) ? '—' : <Link href={`tel:${candidate.phone}`} underline="hover" sx={{ color: '#334155' }}>{candidate.phone}</Link>}
            </InfoLine>
            <InfoLine icon={<PlaceOutlinedIcon />}>{isBlank(candidate.location) ? '—' : candidate.location}</InfoLine>
            {github && (
              <InfoLine icon={<GitHubIcon />}>
                <Link href={github} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#334155' }}>GitHub</Link>
              </InfoLine>
            )}
          </Box>

          <Box sx={{ mt: 1.25, p: 1.25, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <LinkedInIcon sx={{ color: '#0a66c2', fontSize: 20 }} />
              {hasLinkedin ? (
                <Link href={linkedinUrl(candidate) || '#'} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: '0.84rem', fontWeight: 700, color: '#03407b' }}>
                  Profil LinkedIn
                </Link>
              ) : (
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: '#dc2626' }}>LinkedIn manquant</Typography>
              )}
              {checking ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#64748b', fontSize: '0.76rem' }}>
                  <CircularProgress size={12} /> Vérification…
                </Box>
              ) : hasLinkedin && liStatus ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: liStatus.color, fontSize: '0.76rem', fontWeight: 700, '& svg': { fontSize: 15 } }}>
                  {liStatus.icon} {liStatus.label}
                </Box>
              ) : null}
              {candidate.linkedin_status !== 'valid' && !editMode && (
                <Box sx={{ display: 'flex', gap: 1.25, ml: 'auto' }}>
                  {hasLinkedin && <Box component="button" sx={linkBtnSx} onClick={() => validate()} disabled={checking}>Vérifier</Box>}
                  {hasLinkedin && (
                    <Box component="button" sx={linkBtnSx} onClick={() => { setLinkInput(candidate.linkedin_verified_url || ''); setEditMode('manual'); }}>
                      Valider manuellement
                    </Box>
                  )}
                  <Box component="button" sx={linkBtnSx} onClick={() => { setLinkInput(hasLinkedin ? candidate.linkedin : ''); setEditMode('edit'); }}>
                    {hasLinkedin ? 'Modifier' : 'Ajouter'}
                  </Box>
                </Box>
              )}
            </Box>
            {candidate.linkedin_verified_url && candidate.linkedin_status !== 'valid' && !editMode && (
              <Link href={candidate.linkedin_verified_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-block', mt: 0.5, fontSize: '0.76rem', color: '#03407b' }}>
                Lien suggéré par la recherche
              </Link>
            )}
            {editMode && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder="https://linkedin.com/in/…"
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveLink()}
                />
                <AppButton size="small" variant="secondary" onClick={() => setEditMode(null)} disabled={savingLink}>Annuler</AppButton>
                <AppButton size="small" loading={savingLink} disabled={!linkInput.trim()} onClick={saveLink}>
                  {editMode === 'manual' ? 'Valider' : 'Enregistrer'}
                </AppButton>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
          <Fact icon={<WorkOutlinedIcon />} label="Expérience" value={typeof candidate.years_of_experience === 'number' ? `${candidate.years_of_experience} an${candidate.years_of_experience > 1 ? 's' : ''}` : null} />
          <Fact icon={<SchoolOutlinedIcon />} label="Formation" value={isBlank(candidate.education_level) ? null : candidate.education_level} />
          <Fact icon={<BusinessOutlinedIcon />} label="Employeur" value={isBlank(candidate.employer) ? null : candidate.employer} />
          <Fact icon={<ApartmentOutlinedIcon />} label="École" value={isBlank(candidate.school) ? null : candidate.school} />
        </Box>

        {breakdown.length > 0 && (
          <Box>
            <SectionTitle>Détail du score</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {breakdown.map(([key, value]) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography noWrap sx={{ width: 110, fontSize: '0.76rem', color: '#64748b', textTransform: 'capitalize' }}>
                    {key.replace(/_vector$/, '').replace(/_/g, ' ')}
                  </Typography>
                  <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${Math.min(value * 100, 100)}%`, bgcolor: '#03407b', borderRadius: 3 }} />
                  </Box>
                  <Typography sx={{ width: 30, textAlign: 'right', fontSize: '0.76rem', fontWeight: 700, color: '#334155' }}>{(value * 10).toFixed(1)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {asArray(candidate.roles_held).length > 0 && (
          <Box><SectionTitle>Postes occupés</SectionTitle><TagList items={candidate.roles_held} max={12} color="#334155" bg="#f1f5f9" /></Box>
        )}
        {asArray(candidate.technical_skills).length > 0 && (
          <Box><SectionTitle>Compétences techniques</SectionTitle><TagList items={candidate.technical_skills} max={30} /></Box>
        )}
        {asArray(candidate.programming_languages).length > 0 && (
          <Box><SectionTitle>Langages</SectionTitle><TagList items={candidate.programming_languages} max={20} color="#0556a4" bg="#f0f7fd" /></Box>
        )}
        {asArray(candidate.spoken_languages).length > 0 && (
          <Box><SectionTitle>Langues parlées</SectionTitle><TagList items={candidate.spoken_languages} max={10} color="#16a34a" bg="#f0fdf4" /></Box>
        )}
        {asArray(candidate.certifications).length > 0 && (
          <Box><SectionTitle>Certifications</SectionTitle><TagList items={candidate.certifications} max={10} color="#7c3aed" bg="#f5f3ff" /></Box>
        )}
        {!isBlank(candidate.summary) && (
          <Box>
            <SectionTitle>Résumé</SectionTitle>
            <Typography sx={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{candidate.summary}</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eef2f7', bgcolor: '#fbfdff' }}>
        {candidate.added_to_crm ? (
          <AppButton
            fullWidth
            variant="outline"
            startIcon={<OpenInNewIcon />}
            disabled={!candidate.crm_url}
            onClick={() => onOpenCrmUrl(candidate.crm_url)}
          >
            {candidate.crm_url ? 'Voir la fiche dans le CRM' : 'Déjà ajouté au CRM'}
          </AppButton>
        ) : (
          <>
            {!canAddToCrm && (
              <Typography sx={{ fontSize: '0.74rem', color: '#b45309', mb: 1, textAlign: 'center' }}>{crmBlockReason}</Typography>
            )}
            <AppButton fullWidth startIcon={<PersonAddAlt1OutlinedIcon />} disabled={!canAddToCrm} onClick={() => setCrmOpen(true)}>
              Ajouter au CRM
            </AppButton>
          </>
        )}
      </Box>

      <CvPreviewModal open={cvOpen} candidate={candidate} onClose={() => setCvOpen(false)} />
      <AddToCrmModal open={crmOpen} candidate={candidate} loading={crmLoading} onClose={() => setCrmOpen(false)} onSubmit={submitCrm} />
    </Box>
  );
};

export default CandidateDetail;
